import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Paper,
  Slider,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useDispatch } from "react-redux";
import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PageLayout from "../../components/PageLayout";
import { showToast } from "../../store/slices/common";
import {
  getSeasonSettings,
  previewSeasonReset,
  runSeasonReset,
  updateSeasonSettings,
} from "../../http/season";

const APP_TIMEZONE = "America/New_York";
const DEFAULT_MMR = 1200;

/** Public tier thresholds (mirror of backend RANK_ELO_RANGES). */
const TIER_FLOORS = [
  ["IRON", 0],
  ["BRONZE", 800],
  ["SILVER", 1250],
  ["GOLD", 1600],
  ["PLATINUM", 2000],
  ["DIAMOND", 2400],
];
const TIER_ORDER = ["UNRANKED", ...TIER_FLOORS.map(([t]) => t), "LEGEND", "GRANDLEGEND", "IDOLIZED"];

const TIER_COLORS = {
  UNRANKED: "#9e9e9e",
  IRON: "#8d6e63",
  BRONZE: "#cd7f32",
  SILVER: "#9fa8b4",
  GOLD: "#f1c40f",
  PLATINUM: "#2ecc9a",
  DIAMOND: "#5dade2",
  LEGEND: "#b388ff",
  GRANDLEGEND: "#2563EB",
  IDOLIZED: "#EC4899",
};

const hexToRgba = (hex, alpha) => {
  const h = hex.replace("#", "");
  const n = parseInt(h, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

const cardSx = (accent) => ({
  padding: "20px",
  borderRadius: "10px",
  boxShadow: "rgba(0, 0, 0, 0.05) 0 6px 24px 0, rgba(0, 0, 0, 0.08) 0 0 0 1px",
  boxSizing: "border-box",
  height: "100%",
  backgroundColor: hexToRgba(accent, 0.06),
  borderTop: `5px solid ${accent}`,
});

const headCellSx = {
  fontWeight: 700,
  color: "#142C5C",
  backgroundColor: "#f4f6fb",
  fontSize: "13px",
};

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("en-US", { timeZone: APP_TIMEZONE, timeZoneName: "short" });
};

const formatUtc = (value) => {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().replace("T", " ").slice(0, 16) + " UTC";
};

/**
 * `<input type="datetime-local">` has no timezone. Treat what the admin types
 * as app-timezone wall-clock time and convert to a UTC instant, so "Jan 31
 * 23:59" means the end of Jan 31 in New York regardless of the admin's laptop.
 */
const zonedLocalToIso = (local) => {
  if (!local) return null;
  const [datePart, timePart = "00:00"] = local.split("T");
  const [y, m, d] = datePart.split("-").map(Number);
  const [hh, mm] = timePart.split(":").map(Number);
  // Start from the naive UTC guess, then correct by the zone offset at that
  // instant. The wall-clock reading is rebuilt with Date.UTC rather than
  // `new Date(string)`, which would parse it in the admin's laptop timezone
  // and only be right when that laptop runs on UTC.
  const guess = new Date(Date.UTC(y, m - 1, d, hh, mm, 0));
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: APP_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(guess);
  const get = (type) => Number(parts.find((p) => p.type === type)?.value);
  const wallAsUtc = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour") % 24,
    get("minute"),
    get("second")
  );
  const offsetMs = guess.getTime() - wallAsUtc;
  return new Date(guess.getTime() + offsetMs).toISOString();
};

const isoToZonedLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: APP_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const get = (type) => parts.find((p) => p.type === type)?.value;
  const hour = get("hour") === "24" ? "00" : get("hour");
  return `${get("year")}-${get("month")}-${get("day")}T${hour}:${get("minute")}`;
};

const tierFromMMR = (mmr) => {
  let tier = "IRON";
  for (const [t, floor] of TIER_FLOORS) if (mmr >= floor) tier = t;
  return tier;
};

const softReset = (mmr, factor) => Math.round(DEFAULT_MMR + (mmr - DEFAULT_MMR) * factor);

const EXAMPLE_MMRS = [2600, 2200, 1800, 1400, 1000];

const RESET_STATUS_CHIP = {
  idle: { label: "Scheduled", color: "default" },
  running: { label: "Running…", color: "warning" },
  completed: { label: "Completed", color: "success" },
  failed: { label: "Failed", color: "error" },
};

const countdownLabel = (endsAt, serverTime) => {
  if (!endsAt) return "No end date set";
  const end = new Date(endsAt).getTime();
  const now = serverTime ? new Date(serverTime).getTime() : Date.now();
  const diff = end - now;
  if (diff <= 0) return "Ended — reset pending";
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  if (days > 0) return `${days}d ${hours}h left`;
  if (hours > 0) return `${hours}h ${mins}m left`;
  return `${mins}m left`;
};

/**
 * Seasons admin page: edit the active season (name, end date, announcement,
 * soft-reset rules), preview the reset's effect on the ladder, and run it
 * early. The scheduler runs the same reset automatically at the end date.
 */
const ManageSeason = () => {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [season, setSeason] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [serverTime, setServerTime] = useState(null);

  // Form state
  const [name, setName] = useState("");
  const [endsAtLocal, setEndsAtLocal] = useState("");
  const [hasEndDate, setHasEndDate] = useState(true);
  const [announcementEnabled, setAnnouncementEnabled] = useState(true);
  const [factor, setFactor] = useState(0.5);
  const [resetGroups, setResetGroups] = useState(true);

  const applySeason = useCallback((s) => {
    if (!s) return;
    setSeason(s);
    setName(s.name || "");
    setHasEndDate(Boolean(s.endsAt));
    setEndsAtLocal(isoToZonedLocal(s.endsAt));
    setAnnouncementEnabled(s.announcementEnabled !== false);
    setFactor(typeof s.mmrSoftResetFactor === "number" ? s.mmrSoftResetFactor : 0.5);
    setResetGroups(s.resetGroups !== false);
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getSeasonSettings();
      if (data.status) {
        applySeason(data.data.season);
        setSeasons(data.data.seasons || []);
        setServerTime(data.data.serverTime || null);
      }
    } catch (error) {
      console.error("Error fetching season settings:", error);
      dispatch(
        showToast({
          color: "error",
          msg: error?.response?.data?.message || "Failed to load season settings.",
        })
      );
    } finally {
      setLoading(false);
    }
  }, [applySeason, dispatch]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const buildPayload = () => {
    if (!name.trim()) {
      dispatch(showToast({ color: "error", msg: "Season name is required." }));
      return null;
    }
    if (hasEndDate && !endsAtLocal) {
      dispatch(showToast({ color: "error", msg: "Pick an end date, or turn off the end date." }));
      return null;
    }
    return {
      name: name.trim(),
      endsAt: hasEndDate ? zonedLocalToIso(endsAtLocal) : null,
      announcementEnabled,
      mmrSoftResetFactor: factor,
      resetGroups,
    };
  };

  const handleSave = async () => {
    const payload = buildPayload();
    if (!payload) return;
    try {
      setSaving(true);
      const data = await updateSeasonSettings(payload);
      if (data.status) {
        applySeason(data.data.season);
        dispatch(showToast({ color: "success", msg: "Season settings saved." }));
        fetchSettings();
      } else {
        dispatch(showToast({ color: "error", msg: data.message || "Failed to save." }));
      }
    } catch (error) {
      console.error("Error saving season settings:", error);
      dispatch(
        showToast({
          color: "error",
          msg: error?.response?.data?.message || "Failed to save season settings.",
        })
      );
    } finally {
      setSaving(false);
    }
  };

  // ---- Preview --------------------------------------------------------------
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const loadPreview = useCallback(
    async (f) => {
      try {
        setPreviewLoading(true);
        const data = await previewSeasonReset(f);
        if (data.status) setPreview(data.data.preview);
      } catch (error) {
        console.error("Error loading reset preview:", error);
        dispatch(
          showToast({
            color: "error",
            msg: error?.response?.data?.message || "Failed to load reset preview.",
          })
        );
      } finally {
        setPreviewLoading(false);
      }
    },
    [dispatch]
  );

  // Read the latest factor without re-running this effect on every slider move.
  // Preview refreshes when settings finish loading; the slider has its own button.
  const factorRef = useRef(factor);
  factorRef.current = factor;

  useEffect(() => {
    if (!loading) loadPreview(factorRef.current);
  }, [loading, loadPreview]);

  // ---- Run reset ----------------------------------------------------------
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [typedName, setTypedName] = useState("");
  const [running, setRunning] = useState(false);

  const handleRunReset = async () => {
    if (!season || typedName.trim() !== season.name) return;
    try {
      setRunning(true);
      const data = await runSeasonReset(typedName.trim());
      if (data.status) {
        dispatch(
          showToast({
            color: "success",
            msg: `${season.name} ended. ${data.data.season?.name || "Next season"} is now active.`,
          })
        );
        setConfirmOpen(false);
        setTypedName("");
        applySeason(data.data.season);
        setSeasons(data.data.seasons || []);
        setPreview(null);
        loadPreview(data.data.season?.mmrSoftResetFactor);
      } else {
        dispatch(showToast({ color: "error", msg: data.message || "Reset failed." }));
      }
    } catch (error) {
      console.error("Season reset failed:", error);
      dispatch(
        showToast({
          color: "error",
          msg: error?.response?.data?.message || "Season reset failed.",
        })
      );
      fetchSettings();
    } finally {
      setRunning(false);
    }
  };

  // ---- Derived ----------------------------------------------------------------
  const example = useMemo(
    () =>
      EXAMPLE_MMRS.map((mmr) => {
        const after = softReset(mmr, factor);
        return { mmr, after, from: tierFromMMR(mmr), to: tierFromMMR(after) };
      }),
    [factor]
  );

  const resetChip = RESET_STATUS_CHIP[season?.resetStatus] || RESET_STATUS_CHIP.idle;
  const endsAtIso = hasEndDate ? zonedLocalToIso(endsAtLocal) : null;
  const dirtyEndDate = (season?.endsAt || null) !== endsAtIso && !(season && !season.endsAt && !endsAtIso);
  const isRunning = season?.resetStatus === "running";
  const isFailed = season?.resetStatus === "failed";
  const previousSeasons = seasons.filter((s) => s.status === "ended");

  return (
    <PageLayout
      title="Seasons"
      subtitle="Competitive seasons end with a rank reset: every player's final rank is saved, hidden MMR is softened toward the middle, and everyone plays 5 placement pagents into the new season. The app shows the season name and countdown on the leaderboard."
      loading={loading}
      actions={
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving || loading || isRunning}
        >
          {saving ? "Saving…" : "Save season"}
        </Button>
      }
    >
      {isFailed && (
        <Alert severity="error" sx={{ mb: 2 }}>
          The last reset attempt failed{season?.resetSummary?.error ? `: ${season.resetSummary.error}` : "."}{" "}
          Players already reset are skipped on retry — use “Run reset now” below to finish the rollover.
        </Alert>
      )}
      {isRunning && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          The season reset is running (started {formatDate(season?.resetStartedAt)}). Settings are
          locked until it finishes.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* ---------------- Current season ---------------- */}
        <Grid item xs={12} md={7}>
          <Paper sx={cardSx("#FFD600")}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1 }}>
                Current season
              </Typography>
              <Chip size="small" label={`#${season?.number ?? "—"}`} />
              <Chip size="small" label={resetChip.label} color={resetChip.color} />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Started {formatDate(season?.startsAt)}.{" "}
              <strong>{countdownLabel(season?.endsAt, serverTime)}</strong>
              {season?.endsAt ? ` · ends ${formatDate(season.endsAt)}` : ""}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={5}>
                <TextField
                  label="Season name"
                  size="small"
                  fullWidth
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  inputProps={{ maxLength: 40 }}
                  helperText="Shown in the app, e.g. “Season 1”."
                  disabled={isRunning}
                />
              </Grid>
              <Grid item xs={12} sm={7}>
                <TextField
                  label={`End date & time (${APP_TIMEZONE})`}
                  type="datetime-local"
                  size="small"
                  fullWidth
                  value={endsAtLocal}
                  onChange={(e) => setEndsAtLocal(e.target.value)}
                  disabled={!hasEndDate || isRunning}
                  InputLabelProps={{ shrink: true }}
                  helperText={
                    hasEndDate && endsAtIso
                      ? `= ${formatUtc(endsAtIso)}. The reset runs within 5 minutes of this moment.`
                      : "Leave off while the end date is undecided — no countdown is shown."
                  }
                />
              </Grid>
            </Grid>

            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Switch
                  checked={hasEndDate}
                  onChange={(e) => setHasEndDate(e.target.checked)}
                  disabled={isRunning}
                />
              }
              label="Season has an end date"
            />
            <FormControlLabel
              sx={{ mt: 1, display: "flex" }}
              control={
                <Switch
                  checked={announcementEnabled}
                  onChange={(e) => setAnnouncementEnabled(e.target.checked)}
                  disabled={isRunning}
                />
              }
              label="Show the one-time “season ending” notice to existing users"
            />
            <Typography variant="caption" color="text.secondary" display="block">
              Shown once per account when the app opens, then never again. Accounts created after{" "}
              <strong>{formatDate(season?.announcementStartsAt)}</strong> never see it. After the
              reset, last season's players get a separate one-time “new season” notice with their
              final rank.
            </Typography>

            {dirtyEndDate && hasEndDate && endsAtIso && new Date(endsAtIso) < new Date() && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                That end date is in the past — saving will trigger the reset on the next scheduler
                tick (within 5 minutes).
              </Alert>
            )}
          </Paper>
        </Grid>

        {/* ---------------- Reset rules ---------------- */}
        <Grid item xs={12} md={5}>
          <Paper sx={cardSx("#5dade2")}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Reset rules
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Hidden MMR is pulled toward {DEFAULT_MMR}:{" "}
              <code>new = {DEFAULT_MMR} + (old − {DEFAULT_MMR}) × factor</code>. Visible rank goes
              to Unranked and placements re-rank from the new MMR after 5 pagents.
            </Typography>

            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              MMR soft-reset factor: {factor.toFixed(2)}
            </Typography>
            <Slider
              value={factor}
              min={0.25}
              max={1}
              step={0.05}
              marks={[
                { value: 0.25, label: "0.25 hard" },
                { value: 0.5, label: "0.5" },
                { value: 0.75, label: "0.75" },
                { value: 1, label: "1 keep" },
              ]}
              onChange={(_, v) => setFactor(Array.isArray(v) ? v[0] : v)}
              disabled={isRunning}
              sx={{ mb: 1 }}
            />

            <TableContainer sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}>Before</TableCell>
                    <TableCell sx={headCellSx}>After</TableCell>
                    <TableCell sx={headCellSx}>Places into</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {example.map((row) => (
                    <TableRow key={row.mmr}>
                      <TableCell>
                        {row.mmr}{" "}
                        <Typography component="span" variant="caption" color="text.secondary">
                          ({row.from})
                        </Typography>
                      </TableCell>
                      <TableCell>{row.after}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.to}
                          sx={{
                            backgroundColor: hexToRgba(TIER_COLORS[row.to], 0.18),
                            fontWeight: 700,
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <FormControlLabel
              control={
                <Switch
                  checked={resetGroups}
                  onChange={(e) => setResetGroups(e.target.checked)}
                  disabled={isRunning}
                />
              }
              label="Also reset the Groups ladder"
            />
            <Typography variant="caption" color="text.secondary" display="block">
              Lifetime match counts, all-time peak rank, rank badges and the For-fun ladder are never
              touched. Staff accounts keep their overlay.
            </Typography>
          </Paper>
        </Grid>

        {/* ---------------- Preview ---------------- */}
        <Grid item xs={12} md={7}>
          <Paper sx={cardSx("#2ecc9a")}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1 }}>
                Reset preview
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={() => loadPreview(factor)}
                disabled={previewLoading}
              >
                {previewLoading ? "Computing…" : `Preview at ${factor.toFixed(2)}`}
              </Button>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Where today's {preview?.totalRanked ?? "—"} ranked players sit now vs. the tier they
              would place into after finishing placements (assuming MMR is unchanged by the
              placement games themselves).
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}>Tier</TableCell>
                    <TableCell sx={headCellSx} align="right">
                      Now
                    </TableCell>
                    <TableCell sx={headCellSx} align="right">
                      After reset
                    </TableCell>
                    <TableCell sx={headCellSx} align="right">
                      Change
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {TIER_ORDER.filter((t) => t !== "UNRANKED").map((tier) => {
                    const now = preview?.current?.[tier] ?? 0;
                    const after = preview?.projected?.[tier] ?? 0;
                    const delta = after - now;
                    return (
                      <TableRow key={tier}>
                        <TableCell>
                          <Chip
                            size="small"
                            label={tier}
                            sx={{
                              backgroundColor: hexToRgba(TIER_COLORS[tier], 0.18),
                              fontWeight: 700,
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">{now}</TableCell>
                        <TableCell align="right">{after}</TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            color: delta > 0 ? "success.main" : delta < 0 ? "error.main" : "text.secondary",
                            fontWeight: 600,
                          }}
                        >
                          {delta > 0 ? `+${delta}` : delta}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {preview?.sampleShifts?.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
                Examples:{" "}
                {preview.sampleShifts
                  .slice(0, 4)
                  .map((s) => `${s.from} → ${s.to}`)
                  .join(" · ")}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              LEGEND is earned through the live ladder (top of Diamond), so the projection never
              places anyone straight into it.
            </Typography>
          </Paper>
        </Grid>

        {/* ---------------- Danger zone + history ---------------- */}
        <Grid item xs={12} md={5}>
          <Paper sx={cardSx("#e53935")}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <WarningAmberIcon color="error" fontSize="small" />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                End season now
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Runs the end-of-season reset immediately using the saved rules above (save first).
              This is the same job the scheduler runs at the end date. It cannot be undone.
            </Typography>
            <Button
              variant="contained"
              color="error"
              onClick={() => setConfirmOpen(true)}
              disabled={loading || isRunning || !season}
            >
              {isFailed ? "Retry reset" : "Run reset now"}
            </Button>

            {season?.resetSummary && season.resetStatus !== "idle" && (
              <Alert severity={isFailed ? "error" : "info"} sx={{ mt: 2 }}>
                <Typography variant="caption" component="pre" sx={{ m: 0, whiteSpace: "pre-wrap" }}>
                  {JSON.stringify(season.resetSummary, null, 1)}
                </Typography>
              </Alert>
            )}

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
              Previous seasons
            </Typography>
            {previousSeasons.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                None yet — this is the first season.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headCellSx}>Season</TableCell>
                      <TableCell sx={headCellSx}>Ended</TableCell>
                      <TableCell sx={headCellSx} align="right">
                        Players reset
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previousSeasons.map((s) => (
                      <TableRow key={s.number}>
                        <TableCell>
                          <Tooltip title={`Started ${formatDate(s.startsAt)} · factor ${s.mmrSoftResetFactor}`}>
                            <span>
                              #{s.number} {s.name}
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{formatDate(s.resetCompletedAt || s.endsAt)}</TableCell>
                        <TableCell align="right">
                          {s.resetSummary?.users?.reset ?? "—"}
                          {s.resetSummary?.groups ? ` (+${s.resetSummary.groups.reset} groups)` : ""}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Dialog
        open={confirmOpen}
        onClose={() => (running ? null : setConfirmOpen(false))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>End {season?.name} now?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Every player's final rank is saved, hidden MMR is softened by{" "}
            <strong>×{season?.mmrSoftResetFactor}</strong>, everyone returns to Unranked
            {season?.resetGroups ? " (groups included)" : ""}, and{" "}
            <strong>Season {(season?.number ?? 0) + 1}</strong> opens immediately. Players get the
            “new season” notice on their next app open.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Type <strong>{season?.name}</strong> to confirm:
          </Typography>
          <TextField
            size="small"
            fullWidth
            autoFocus
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
            placeholder={season?.name}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={running}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRunReset}
            disabled={running || typedName.trim() !== season?.name}
          >
            {running ? "Resetting…" : "End season"}
          </Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
};

export default ManageSeason;
